import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Observable, from, of, timer, TimeoutError, throwError } from 'rxjs';
import { mergeMap, catchError, timeout, map, take, first, tap } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';
import { InteractionRequiredAuthError, AccountInfo, AuthenticationResult } from '@azure/msal-browser';
import { environment } from '../environments/environment';
import { AcquireStatus, DiagSnapshot, SubmitDiagnosticsService } from './services/diagnostics/submit-diagnostics.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  // Intentionally empty — all endpoints now require a Bearer token.
  // Kept in place so a public endpoint can be re-added without structural changes.
  private readonly excludedEndpoints: string[] = [];

  // On a cold load MSAL may not have hydrated/marked the active account by the time
  // the first protected request fires. Rather than immediately sending token-less
  // (which the guarded revision 401s — logged as reason 'no-account'), briefly poll
  // for the account to appear. Sized to comfortably cover the observed init race —
  // prod analysis 2026-07-17 showed no-account retries succeeding within 1–6s — while
  // still bounding how long a genuinely signed-out request is delayed (~2.4s).
  private readonly accountWaitAttempts = 6;
  private readonly accountWaitIntervalMs = 400;

  // Transient silent-acquire failures (timeout/silent-error) get one short-backoff retry
  // before the request is blocked. Sized to ride out a brief network blip without
  // materially delaying the eventual draft-preserving error.
  private readonly acquireRetryBackoffMs = 300;

  constructor(
    private msalService: MsalService,
    private diag: SubmitDiagnosticsService,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isExcluded(req)) {
      return next.handle(req);
    }

    // Capture a diagnostics snapshot for the monitored form POSTs BEFORE we wait
    // for MSAL to hydrate, so it reflects the auth/browser state that produced the
    // outcome. Finalized (and beaconed) once the response status is known. This
    // never touches the outgoing request.
    const form = this.diag.formForUrl(req.method, req.url);
    const snap: DiagSnapshot | null = form ? this.diag.snapshot(form) : null;

    const result$ = this.resolveAccount().pipe(
      mergeMap((account) => {
        if (!account) {
          // Waited for MSAL to hydrate and still no account — genuinely signed out.
          // Do NOT send the request token-less (a guaranteed 401, and data loss on a
          // form POST). Block it instead: MsalGuard owns interactive re-auth at route
          // entry, and the form's error handler surfaces draft-preserving guidance.
          if (snap) snap.acquireStatus = 'no-account';
          return throwError(() => this.authBlockedError('no-account'));
        }
        return this.attachToken(account, req, next, snap);
      }),
    );

    if (!snap) {
      return result$;
    }

    return result$.pipe(
      tap({
        next: (event) => {
          if (event instanceof HttpResponse) {
            this.diag.reportOutcome(snap, event.status);
          }
        },
        error: (err) => {
          const status = err instanceof HttpErrorResponse ? err.status : 0;
          this.diag.reportOutcome(snap, status);
        },
      }),
    );
  }

  // Resolves the active MSAL account, tolerating the account-init race:
  //  - falls back to getAllAccounts()[0] (promoting it to active), and
  //  - if no account exists yet, polls briefly for MSAL to hydrate one before
  //    giving up. Emits null only when the user is genuinely signed out.
  private resolveAccount(): Observable<AccountInfo | null> {
    const immediate = this.currentAccount();
    if (immediate) {
      return of(immediate);
    }

    return timer(0, this.accountWaitIntervalMs).pipe(
      take(this.accountWaitAttempts),
      map(() => this.currentAccount()),
      // Emit the first non-null account; if the poll window elapses with none,
      // emit null (default) so the caller can fall through to token-less.
      first((account): account is AccountInfo => !!account, null),
    );
  }

  // Returns the active account, falling back to the first cached account and
  // promoting it to active so later reads (this request and subsequent ones)
  // are consistent.
  private currentAccount(): AccountInfo | null {
    const instance = this.msalService.instance;
    let account = instance.getActiveAccount();
    if (!account) {
      account = instance.getAllAccounts()[0] ?? null;
      if (account) {
        instance.setActiveAccount(account);
      }
    }
    return account;
  }

  private attachToken(
    account: AccountInfo,
    req: HttpRequest<any>,
    next: HttpHandler,
    snap: DiagSnapshot | null,
  ): Observable<HttpEvent<any>> {
    return this.acquireWithRetry(account).pipe(
      // catchError here scopes ONLY to token acquisition (it precedes the mergeMap that
      // calls next.handle), so real backend HTTP errors propagate to the caller untouched.
      catchError((error) => {
        let reason: AcquireStatus = 'silent-error';
        if (error instanceof TimeoutError) {
          reason = 'timeout';
        } else if (error instanceof InteractionRequiredAuthError) {
          reason = 'interaction-required';
        }
        if (snap) snap.acquireStatus = reason;
        console.error('Token acquisition failed:', error);

        if (reason === 'interaction-required') {
          // Dead refresh token — a silent retry cannot fix it. Escalate to interactive
          // re-auth (full-page redirect). The form draft is persisted at submit time, so
          // it survives the redirect and is restored on return.
          this.msalService.instance.acquireTokenRedirect({
            scopes: environment.apiConfig.scopes,
            account,
            // Return to the form after re-auth so the persisted draft can be restored.
            redirectStartPage: typeof window !== 'undefined' ? window.location.href : undefined,
          });
        }

        // Fail CLOSED: block the request with a synthetic 401 the form error handlers map
        // to the session-expired / draft-preserved path. Never send a request we already
        // know lacks a token (the destructive token-less send this replaces).
        return throwError(() => this.authBlockedError(reason));
      }),
      mergeMap((result: AuthenticationResult) => {
        if (snap) snap.acquireStatus = 'ok';
        const authReq = req.clone({
          setHeaders: { Authorization: `Bearer ${result.accessToken}` },
        });
        return next.handle(authReq);
      }),
    );
  }

  // One silent acquisition attempt, bounded to 5s.
  private acquireSilent(account: AccountInfo): Observable<AuthenticationResult> {
    return from(this.msalService.acquireTokenSilent({
      scopes: environment.apiConfig.scopes,
      account,
    })).pipe(timeout(5000));
  }

  // Transient acquire failures (timeout/silent-error) get one short-backoff retry before
  // giving up. interaction-required is NOT retried — a dead refresh token needs interaction,
  // so it propagates immediately to attachToken's classifier for the redirect path.
  private acquireWithRetry(account: AccountInfo): Observable<AuthenticationResult> {
    return this.acquireSilent(account).pipe(
      catchError((error) => {
        if (error instanceof InteractionRequiredAuthError) {
          return throwError(() => error);
        }
        return timer(this.acquireRetryBackoffMs).pipe(
          mergeMap(() => this.acquireSilent(account)),
        );
      }),
    );
  }

  // Synthetic auth-blocked error. status 401 so existing form handlers map it to the
  // session-expired / "your data has been preserved" path without special-casing.
  private authBlockedError(reason: string): HttpErrorResponse {
    return new HttpErrorResponse({
      status: 401,
      statusText: 'Authentication required',
      error: { blockedByInterceptor: true, reason },
    });
  }

  private isExcluded(req: HttpRequest<any>): boolean {
    if (req.method !== 'POST') return false;
    const base = environment.apiConfig.uri;
    return this.excludedEndpoints.some(ep => {
      const target = `${base}/${ep}`;
      return req.url === target || req.url.startsWith(`${target}?`);
    });
  }
}
